// SQLite service for Object-Property-LWW-value persistence
import initSqlJs from 'sql.js';
import type { ObjectEntity, ObjectProperty } from '../types/shared-types';
import { clientStateManager } from './ClientStateManager';
import { initializePropertyDrivenDatabase } from '@object-property-lww/database-schema';
import { SharedDatabaseUtils } from '@object-property-lww/database-utils';

export class SQLiteService {
  private db: any = null;
  private SQL: any = null;
  private databaseName: string = '';
  private dbUtils: SharedDatabaseUtils | null = null;
  private batchMode: boolean = false;

  async initialize(databaseName: string): Promise<void> {
    try {
      // Store document databases in a subfolder
      this.databaseName = databaseName.includes('.db') ? `documents/${databaseName}` : `documents/${databaseName}.db`;
      
      // Initialize sql.js
      this.SQL = await initSqlJs({
        locateFile: (file: string) => {
          if (file === 'sql-wasm.wasm') {
            return '/sql-wasm.wasm';
          }
          return file;
        }
      });

      // Try to load existing database from OPFS/localStorage or create new one
      const existingData = await this.loadDatabaseFromStorage();
      if (existingData) {
        this.db = new this.SQL.Database(existingData);
      } else {
        this.db = new this.SQL.Database();
      }

      // Create the object_properties table if it doesn't exist
      await this.createTables();
      
      // Run background garbage collection to clean up any leftover properties
      setTimeout(() => this.garbageCollectOnLaunch(), 1000);
    } catch (error) {
      console.error('Failed to initialize SQLite:', error);
      throw error;
    }
  }

  async initializeFromDatabaseExport(documentId: string, databaseExport: any): Promise<void> {
    try {
      // Set up database name and SQL.js
      this.databaseName = `documents/${documentId}.db`;
      
      if (!this.SQL) {
        this.SQL = await initSqlJs({
          locateFile: (file: string) => {
            if (file === 'sql-wasm.wasm') {
              return '/sql-wasm.wasm';
            }
            return file;
          }
        });
      }

      // Create a fresh database
      this.db = new this.SQL.Database();
      
      // Create the schema
      await this.createTables();

      // Import objects and properties with UI-responsive chunked processing
      const objects = databaseExport.objects || [];
      const properties = databaseExport.properties || [];
      const currentTime = Date.now();

      console.log(`📥 [SQLITE] Starting import: ${objects.length} objects, ${properties.length} properties`);
      const importStartTime = performance.now();

      // Use a transaction for consistency
      this.db.exec('BEGIN TRANSACTION');

      try {
        // Import objects first
        if (objects.length > 0) {
          console.log(`📥 [SQLITE] Building objects SQL...`);
          const objectsStartTime = performance.now();
          
          const objectValues = objects.map((obj: any) => 
            `('${obj.object_id.replace(/'/g, "''")}', ` +
            `${obj.parent_id ? `'${obj.parent_id.replace(/'/g, "''")}'` : 'NULL'}, ` +
            `'${obj.object_type.replace(/'/g, "''")}', ` +
            `${obj.is_tombstoned ? 1 : 0}, ` +
            `${obj.created_at || currentTime}, ` +
            `${obj.created_by_replica || 0}, ` +
            `'${(obj.created_by_user || 'downloaded').replace(/'/g, "''")}', ` +
            `1)`
          ).join(',\n');

          console.log(`📥 [SQLITE] Objects SQL built in ${(performance.now() - objectsStartTime).toFixed(1)}ms, executing...`);
          
          const bulkObjectsSQL = `
            INSERT INTO objects (object_id, parent_id, object_type, is_tombstoned, created_at, created_by_replica, created_by_user, dirty)
            VALUES ${objectValues}
          `;

          this.db.exec(bulkObjectsSQL);
          console.log(`📥 [SQLITE] ✅ Inserted ${objects.length} objects in ${(performance.now() - objectsStartTime).toFixed(1)}ms`);
        }

        // Import properties using bulk approach with disabled triggers
        console.log(`📥 [SQLITE] Starting properties import with disabled triggers for maximum speed`);

        // End the current transaction
        this.db.exec('COMMIT');

        // Disable triggers during bulk import for maximum performance
        console.log(`📥 [SQLITE] Disabling triggers for bulk import...`);
        this.db.exec('DROP TRIGGER IF EXISTS create_object_from_metadata');
        this.db.exec('DROP TRIGGER IF EXISTS update_object_tombstone');
        this.db.exec('DROP TRIGGER IF EXISTS mark_object_dirty_on_property_change');

        // Process properties in larger batches since triggers are disabled
        const batchSize = 2000; // Much larger batches without trigger overhead
        const totalBatches = Math.ceil(properties.length / batchSize);
        
        console.log(`📥 [SQLITE] Processing ${totalBatches} batches of ${batchSize} properties each (triggers disabled)`);

        for (let i = 0; i < properties.length; i += batchSize) {
          const batchStartTime = performance.now();
          const batch = properties.slice(i, i + batchSize);
          const batchNumber = Math.floor(i / batchSize) + 1;
          
          console.log(`📥 [SQLITE] Processing batch ${batchNumber}/${totalBatches} (${batch.length} properties)...`);
          
          // Start a new transaction for this batch
          this.db.exec('BEGIN TRANSACTION');
          
          try {
            // Use a prepared statement for this batch
            const propertiesStmt = this.db.prepare(`
              INSERT INTO object_properties (object_id, property, value, lww_counter, lww_random, replica_id, logical_timestamp, updated_at, user_id)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
            `);

            for (const prop of batch) {
              propertiesStmt.run([
                prop.object_id,
                prop.property,
                prop.value || null,
                prop.lww_counter || null,
                prop.lww_random || null,
                prop.replica_id,
                prop.logical_timestamp,
                prop.updated_at || currentTime,
                prop.user_id || 'downloaded'
              ]);
            }
            
            propertiesStmt.free();
            this.db.exec('COMMIT');
            
          } catch (error) {
            this.db.exec('ROLLBACK');
            throw error;
          }
          
          const batchTime = performance.now() - batchStartTime;
          const progress = Math.min(i + batchSize, properties.length);
          const progressPercent = ((progress / properties.length) * 100).toFixed(1);
          
          console.log(`📥 [SQLITE] ✅ Batch ${batchNumber}/${totalBatches} completed in ${batchTime.toFixed(1)}ms (${progressPercent}% - ${progress}/${properties.length})`);
          
          // Yield to UI after every batch to keep it responsive
          if (batchNumber % 1 === 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }

        // Re-create triggers after bulk import
        console.log(`📥 [SQLITE] Re-creating triggers...`);
        this.db.exec(`
          CREATE TRIGGER IF NOT EXISTS create_object_from_metadata
            AFTER INSERT ON object_properties
            WHEN NEW.property = 'object.metadata'
          BEGIN
            INSERT OR IGNORE INTO objects (
              object_id, 
              parent_id, 
              object_type, 
              is_tombstoned,
              created_at, 
              created_by_replica, 
              created_by_user,
              dirty,
              updated_at
            )
            SELECT 
              NEW.object_id,
              JSON_EXTRACT(NEW.value, '$.parent_id'),
              JSON_EXTRACT(NEW.value, '$.type'),
              CASE 
                WHEN EXISTS (
                  SELECT 1 FROM tombstone_ancestry 
                  WHERE object_id = COALESCE(JSON_EXTRACT(NEW.value, '$.parent_id'), '')
                    AND has_tombstoned_ancestor = 1
                ) THEN 1
                ELSE 0
              END,
              NEW.updated_at,
              NEW.replica_id,
              NEW.user_id,
              1,
              NEW.updated_at;
          END
        `);

        this.db.exec(`
          CREATE TRIGGER IF NOT EXISTS update_object_tombstone
            AFTER INSERT ON object_properties
            WHEN NEW.property = 'object.tombstoned' AND NEW.value = 'true'
          BEGIN
            UPDATE objects 
            SET is_tombstoned = 1 
            WHERE object_id = NEW.object_id;
          END
        `);

        this.db.exec(`
          CREATE TRIGGER IF NOT EXISTS mark_object_dirty_on_property_change
            AFTER INSERT ON object_properties
          BEGIN
            UPDATE objects
            SET dirty = 1, updated_at = (strftime('%s', 'now') * 1000)
            WHERE object_id = NEW.object_id;
          END
        `);

        const totalTime = performance.now() - importStartTime;
        console.log(`📥 [SQLITE] 🎉 Import completed successfully in ${totalTime.toFixed(1)}ms (${(totalTime/1000).toFixed(1)}s)`);

      } catch (error) {
        const totalTime = performance.now() - importStartTime;
        console.error(`📥 [SQLITE] ❌ Import failed after ${totalTime.toFixed(1)}ms:`, error);
        
        // Try to re-create triggers even on error
        try {
          console.log(`📥 [SQLITE] Re-creating triggers after error...`);
          this.db.exec(`
            CREATE TRIGGER IF NOT EXISTS create_object_from_metadata
              AFTER INSERT ON object_properties
              WHEN NEW.property = 'object.metadata'
            BEGIN
              INSERT OR IGNORE INTO objects (
                object_id, 
                parent_id, 
                object_type, 
                is_tombstoned,
                created_at, 
                created_by_replica, 
                created_by_user,
                dirty,
                updated_at
              )
              SELECT 
                NEW.object_id,
                JSON_EXTRACT(NEW.value, '$.parent_id'),
                JSON_EXTRACT(NEW.value, '$.type'),
                0,
                NEW.updated_at,
                NEW.replica_id,
                NEW.user_id,
                1,
                NEW.updated_at;
            END
          `);

          this.db.exec(`
            CREATE TRIGGER IF NOT EXISTS update_object_tombstone
              AFTER INSERT ON object_properties
              WHEN NEW.property = 'object.tombstoned' AND NEW.value = 'true'
            BEGIN
              UPDATE objects 
              SET is_tombstoned = 1 
              WHERE object_id = NEW.object_id;
            END
          `);

          this.db.exec(`
            CREATE TRIGGER IF NOT EXISTS mark_object_dirty_on_property_change
              AFTER INSERT ON object_properties
            BEGIN
              UPDATE objects
              SET dirty = 1, updated_at = (strftime('%s', 'now') * 1000)
              WHERE object_id = NEW.object_id;
            END
          `);
        } catch (triggerError) {
          console.error('Failed to re-create triggers:', triggerError);
        }
        
        throw error;
      }

      // Initialize database utilities first
      if (!this.dbUtils) {
        this.dbUtils = new SharedDatabaseUtils(this.db);
      }

      // Save the database to OPFS immediately as a binary SQLite file
      await this.saveDatabaseToStorage();

      console.log(`📥 [SQLITE] Created local database ${documentId} with ${objects.length} objects and ${properties.length} properties`);

    } catch (error) {
      console.error('Failed to initialize SQLite from database export:', error);
      throw error;
    }
  }

  private async createTables(): Promise<void> {
    // Use shared property-driven database schema
    initializePropertyDrivenDatabase(this.db);
    
    // Initialize shared database utilities
    this.dbUtils = new SharedDatabaseUtils({
      exec: (sql: string, params?: any[]) => {
        // For sql.js, we need to use prepare/step pattern for SELECT queries
        // and exec for non-SELECT queries
        const results: any[] = [];
        
        if (sql.trim().toUpperCase().startsWith('SELECT')) {
          // Use prepared statement for SELECT queries
          const stmt = this.db.prepare(sql);
          if (params && params.length > 0) {
            stmt.bind(params);
          }
          while (stmt.step()) {
            results.push(stmt.getAsObject());
          }
          stmt.free();
        } else {
          // Use exec for non-SELECT queries (INSERT, UPDATE, DELETE, etc.)
          if (params && params.length > 0) {
            this.db.run(sql, params);
          } else {
            this.db.exec(sql);
          }
          // For non-SELECT queries, return empty array
        }
        
        return results;
      },
      prepare: (sql: string) => this.db.prepare(sql),
      getRowsModified: () => this.db.getRowsModified()
    });
    
    // Initialize client state
    await clientStateManager.initialize();
  }

  async createObject(
    objectId: string,
    parentId: string | null,
    objectType: string
  ): Promise<void> {
    // Event-driven object creation: insert object.metadata property
    const metadata = {
      parent_id: parentId,
      type: objectType
    };
    
    await this.insertOrUpdateProperty(objectId, 'object.metadata', JSON.stringify(metadata));
  }

  async tombstoneObject(objectId: string): Promise<void> {
    // Event-driven tombstoning: insert object.tombstoned property
    await this.insertOrUpdateProperty(objectId, 'object.tombstoned', true);
  }

  async deleteProperty(objectId: string, property: string): Promise<void> {
    const sql = `DELETE FROM object_properties WHERE object_id = ? AND property = ?`;
    this.db.run(sql, [objectId, property]);
    
    // Auto-compute hashes after property deletion
    await this.autoComputeHashes();
    
    // Save to persistent storage
    await this.saveDatabaseToStorage();
  }

  async garbageCollect(): Promise<{objectsProcessed: number, propertiesDeleted: number}> {
    if (!this.db || !this.dbUtils) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    
    const result = await this.dbUtils.garbageCollect();
    await this.saveDatabaseToStorage();
    
    return result;
  }

  // Merkle tree hash computation methods
  async processDirtyObjects(): Promise<number> {
    if (!this.dbUtils) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    
    const result = await this.dbUtils.processDirtyObjects();
    await this.saveDatabaseToStorage();
    
    return result;
  }

  async fetchSubtrees(rootObjectIds: string[], maxDepth: number = 10): Promise<any[]> {
    if (!this.dbUtils) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    return await this.dbUtils.fetchSubtrees(rootObjectIds, maxDepth);
  }

  hasDirtyHashes(): boolean {
    if (!this.dbUtils) {
      return false;
    }
    
    return this.dbUtils.hasDirtyHashes();
  }

  async getRootHashForSync(documentId: string): Promise<string> {
    if (!this.dbUtils) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    
    return await this.dbUtils.getRootHashForSync(documentId);
  }

  async getChildrenHashes(parentId: string): Promise<Record<string, string>> {
    if (!this.dbUtils) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    
    return await this.dbUtils.getChildrenHashes(parentId);
  }

  async ensureHashesUpToDate(): Promise<number> {
    if (!this.dbUtils) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    
    if (this.hasDirtyHashes()) {
      const result = await this.processDirtyObjects();
      return result;
    }
    
    return 0;
  }


  private async autoComputeHashes(): Promise<void> {
    try {
      if (this.hasDirtyHashes()) {
        await this.processDirtyObjects();
      }
    } catch (error) {
      console.warn('Auto hash computation failed:', error);
      // Don't throw - we don't want to break the main operation
    }
  }

  async garbageCollectOnLaunch(): Promise<void> {
    try {
      const result = await this.garbageCollect();
      if (result.propertiesDeleted > 0) {
      }
    } catch (error) {
      console.error('Background garbage collection failed:', error);
    }
  }

  async rebuildObjectsTable(): Promise<{objectsCreated: number, errors: string[]}> {
    
    if (!this.db) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
    
    const errors: string[] = [];
    let objectsCreated = 0;
    
    try {
      // Clear existing objects table
      this.db.exec('DELETE FROM objects');
      
      // Get all object.metadata properties
      const metadataStmt = this.db.prepare(`
        SELECT object_id, value, replica_id, updated_at, user_id
        FROM object_properties 
        WHERE property = 'object.metadata'
        ORDER BY updated_at
      `);
      
      while (metadataStmt.step()) {
        const row = metadataStmt.getAsObject();
        try {
          const metadata = JSON.parse(row.value as string);
          const parentId = metadata.parent_id || null;
          const objectType = metadata.type;
          
          if (!objectType) {
            errors.push(`Object ${row.object_id}: missing type in metadata`);
            continue;
          }
          
          // Check if ancestor is tombstoned
          let isTombstoned = 0;
          if (parentId) {
            const ancestryStmt = this.db.prepare(`
              SELECT has_tombstoned_ancestor FROM tombstone_ancestry WHERE object_id = ?
            `);
            ancestryStmt.bind([parentId]);
            if (ancestryStmt.step()) {
              const ancestryResult = ancestryStmt.getAsObject();
              isTombstoned = ancestryResult.has_tombstoned_ancestor ? 1 : 0;
            }
            ancestryStmt.free();
          }
          
          // Check if this object has a tombstone property
          const tombstoneStmt = this.db.prepare(`
            SELECT 1 FROM object_properties 
            WHERE object_id = ? AND property = 'object.tombstoned' AND value = 'true'
          `);
          tombstoneStmt.bind([row.object_id]);
          if (tombstoneStmt.step()) {
            isTombstoned = 1;
          }
          tombstoneStmt.free();
          
          // Insert object
          const insertStmt = this.db.prepare(`
            INSERT OR REPLACE INTO objects (
              object_id, parent_id, object_type, is_tombstoned,
              created_at, created_by_replica, created_by_user
            ) VALUES (?, ?, ?, ?, ?, ?, ?)
          `);
          insertStmt.bind([
            row.object_id,
            parentId,
            objectType,
            isTombstoned,
            row.updated_at,
            row.replica_id,
            row.user_id
          ]);
          insertStmt.step();
          insertStmt.free();
          
          objectsCreated++;
          
        } catch (error) {
          errors.push(`Object ${row.object_id}: ${error}`);
        }
      }
      metadataStmt.free();
      
      if (errors.length > 0) {
        console.warn('Rebuild errors:', errors);
      }
      
      await this.saveDatabaseToStorage();
      return {objectsCreated, errors};
      
    } catch (error) {
      console.error('Failed to rebuild objects table:', error);
      throw error;
    }
  }

  async importProperties(properties: ObjectProperty[]): Promise<void> {
    // Use shared database utils for consistent LWW conflict resolution
    if (!this.dbUtils) {
      throw new Error('Database utils not initialized');
    }
    
    // Check if any tombstone properties are being imported
    const hasTombstoneProperties = properties.some(p => 
      p.property === 'object.tombstoned' && String(p.value) === 'true'
    );
    
    
    // Use batch mode for large imports to avoid excessive saves
    const shouldUseBatch = properties.length > 1;
    if (shouldUseBatch) {
      this.startBatch();
    }
    
    try {
      
      const result = await this.dbUtils.importProperties(properties);
      
      
      if (result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
      }
      
      // Run garbage collection if tombstone properties were imported
      if (hasTombstoneProperties) {
        try {
          const gcResult = await this.garbageCollect();
        } catch (gcError) {
          console.warn('Garbage collection after import failed:', gcError);
        }
      }
      
      // Recompute hashes after importing (only if not in batch mode)
      if (!this.batchMode) {
        await this.autoComputeHashes();
      }
    } finally {
      // Always end batch mode if we started it (this will also compute hashes and save)
      if (shouldUseBatch) {
        await this.endBatch();
      }
    }
  }

  async insertOrUpdateProperty(
    objectId: string,
    property: string,
    value: any
  ): Promise<void> {
    // Convert value to string for SQLite storage
    const stringValue = typeof value === 'object' && value !== null 
      ? JSON.stringify(value) 
      : String(value);
    
    const clientState = clientStateManager.getClientState();
    const isSpecialProperty = property.startsWith('object.');
    
    // For non-special properties, check if object or ancestors are tombstoned
    if (!isSpecialProperty) {
      const ancestryCheck = this.db.prepare(`
        SELECT has_tombstoned_ancestor 
        FROM tombstone_ancestry 
        WHERE object_id = ?
      `);
      ancestryCheck.bind([objectId]);
      
      let ancestry = null;
      if (ancestryCheck.step()) {
        ancestry = ancestryCheck.getAsObject();
      }
      ancestryCheck.free();
      
      if (ancestry?.has_tombstoned_ancestor) {
        return;
      }
    }

    const logicalTimestamp = clientStateManager.getNextLogicalTimestamp();
    const updatedAt = Date.now();

    if (isSpecialProperty) {
      // Special properties: no LWW conflict resolution, INSERT OR IGNORE
      const sql = `
        INSERT OR IGNORE INTO object_properties (
          object_id, property, value, replica_id, logical_timestamp, updated_at, user_id
        ) VALUES (?, ?, ?, ?, ?, ?, ?)
      `;
      
      const params = [
        objectId, property, stringValue,
        clientState.replica_id, logicalTimestamp, updatedAt, clientState.user_id
      ];
      this.db.run(sql, params);
      
      // Update version vector for the object
      if (this.dbUtils) {
        // Note: Object is automatically marked dirty by trigger
        // Version vector will be computed during hash recomputation
      }
    } else {
      // Regular properties: LWW conflict resolution
      const maxCounterSql = `
        SELECT COALESCE(MAX(lww_counter), 0) as max_counter 
        FROM object_properties 
        WHERE object_id = ? AND lww_counter IS NOT NULL
      `;
      const stmt = this.db.prepare(maxCounterSql);
      stmt.bind([objectId]);
      stmt.step();
      const result = stmt.getAsObject();
      stmt.free();
      
      const lwwCounter = (result.max_counter as number) + 1;
      const lwwRandom = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER);
      
      const sql = `
        INSERT INTO object_properties (
          object_id, property, value, lww_counter, lww_random, 
          replica_id, logical_timestamp, updated_at, user_id
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        ON CONFLICT (object_id, property) DO UPDATE SET
          value = excluded.value,
          lww_counter = excluded.lww_counter,
          lww_random = excluded.lww_random,
          replica_id = excluded.replica_id,
          logical_timestamp = excluded.logical_timestamp,
          updated_at = excluded.updated_at,
          user_id = excluded.user_id
        WHERE excluded.lww_counter > object_properties.lww_counter
           OR (excluded.lww_counter = object_properties.lww_counter 
               AND excluded.lww_random > object_properties.lww_random);
      `;

      const params = [
        objectId, property, stringValue, lwwCounter, lwwRandom,
        clientState.replica_id, logicalTimestamp, updatedAt, clientState.user_id
      ];
      this.db.run(sql, params);
      
      // Update version vector for the object
      if (this.dbUtils) {
        // Note: Object is automatically marked dirty by trigger
        // Version vector will be computed during hash recomputation
      }
    }

    // Run garbage collection if a tombstone property was added
    if (property === 'object.tombstoned' && value === true) {
      try {
        // Small delay to ensure database trigger has processed
        await new Promise(resolve => setTimeout(resolve, 10));
        
        const gcResult = await this.garbageCollect();
      } catch (gcError) {
        console.warn('Garbage collection after tombstone failed:', gcError);
      }
    }

    // Auto-compute hashes after property changes (only if not in batch mode)
    if (!this.batchMode) {
      await this.autoComputeHashes();
      // Auto-save after each change for persistence
      await this.saveDatabaseToStorage();
    }
  }

  async getObjects(): Promise<ObjectEntity[]> {
    const sql = `
      SELECT object_id, parent_id, object_type, is_tombstoned, 
             created_at, created_by_replica, created_by_user,
             dirty, content_hash, subtree_hash, version_vector, updated_at
      FROM objects
      ORDER BY created_at;
    `;

    const stmt = this.db.prepare(sql);
    const result = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      result.push({
        object_id: row.object_id as string,
        parent_id: row.parent_id as string || undefined,
        object_type: row.object_type as string,
        is_tombstoned: Boolean(row.is_tombstoned),
        created_at: row.created_at as number,
        created_by_replica: row.created_by_replica as number,
        created_by_user: row.created_by_user as string,
        dirty: Boolean(row.dirty),
        content_hash: row.content_hash as string | null,
        subtree_hash: row.subtree_hash as string | null,
        version_vector: row.version_vector as string | null,
        updated_at: row.updated_at as number | null,
      });
    }
    stmt.free();
    return result;
  }

  async getObjectProperties(objectId: string): Promise<ObjectProperty[]> {
    const sql = `
      SELECT object_id, property, value, lww_counter, lww_random, 
             replica_id, logical_timestamp, updated_at, user_id
      FROM object_properties
      WHERE object_id = ? 
        AND NOT EXISTS (
          SELECT 1 FROM objects 
          WHERE objects.object_id = object_properties.object_id 
            AND objects.is_tombstoned = 1
        )
      ORDER BY property;
    `;

    const stmt = this.db.prepare(sql);
    stmt.bind([objectId]);
    const result = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      result.push({
        object_id: row.object_id as string,
        property: row.property as string,
        value: row.value as string,
        lww_counter: row.lww_counter as number,
        lww_random: row.lww_random as number,
        replica_id: row.replica_id as number,
        logical_timestamp: row.logical_timestamp as number,
        updated_at: row.updated_at as number,
        user_id: row.user_id as string,
      });
    }
    stmt.free();
    return result;
  }

  async getPropertiesSince(timestamp: number): Promise<ObjectProperty[]> {
    const sql = `
      SELECT object_id, property, value, lww_counter, lww_random, 
             replica_id, logical_timestamp, updated_at, user_id
      FROM object_properties
      WHERE updated_at > ?
      ORDER BY updated_at;
    `;

    const stmt = this.db.prepare(sql);
    stmt.bind([timestamp]);
    const result = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      result.push({
        object_id: row.object_id as string,
        property: row.property as string,
        value: row.value as string,
        lww_counter: row.lww_counter as number,
        lww_random: row.lww_random as number,
        replica_id: row.replica_id as number,
        logical_timestamp: row.logical_timestamp as number,
        updated_at: row.updated_at as number,
        user_id: row.user_id as string,
      });
    }
    stmt.free();
    return result;
  }

  /**
   * Get hash information for a single object by ID
   * More efficient than getObjectHashes when you only need one object
   */
  async getSingleObjectHash(objectId: string): Promise<{ objectId: string; contentHash: string; subtreeHash: string; versionVector: Record<string, number> } | null> {
    const sql = `
      SELECT 
        object_id,
        content_hash,
        subtree_hash,
        version_vector
      FROM objects 
      WHERE object_id = ?
    `;
    
    const stmt = this.db.prepare(sql);
    stmt.bind([objectId]);
    
    let result = null;
    if (stmt.step()) {
      result = stmt.getAsObject();
    }
    
    stmt.free();
    
    if (!result) {
      return null;
    }
    
    return {
      objectId: result.object_id || objectId,
      contentHash: result.content_hash || '',
      subtreeHash: result.subtree_hash || '0',
      versionVector: result.version_vector ? JSON.parse(result.version_vector) : {}
    };
  }

  /**
   * Get object hashes and version vectors for sync comparison
   * Returns objects AT the specified depth only, using pre-computed hashes
   */
  async getObjectHashes(documentId: string, depth: number): Promise<Record<string, any>> {
    // Ensure hashes are up-to-date by processing dirty objects first
    await this.processDirtyObjects();
    
    const result: Record<string, any> = {};
    
    const sql = `
      WITH RECURSIVE document_objects AS (
        -- Start with the document root
        SELECT object_id, parent_id, object_type, 0 as depth
        FROM objects 
        WHERE object_id = ?
        
        UNION ALL
        
        -- Recursively find children up to specified depth
        SELECT o.object_id, o.parent_id, o.object_type, do.depth + 1
        FROM objects o
        INNER JOIN document_objects do ON o.parent_id = do.object_id
        WHERE do.depth < ?
      )
      SELECT DISTINCT o.object_id, o.parent_id, o.object_type, 
             o.content_hash, o.subtree_hash, o.version_vector
      FROM document_objects do
      INNER JOIN objects o ON do.object_id = o.object_id
      WHERE do.depth = ?  -- Only return objects AT this specific depth
      ORDER BY o.object_id;
    `;
    
    const stmt = this.db.prepare(sql);
    stmt.bind([documentId, depth + 1, depth]); // depth + 1 for recursion limit, depth for filtering
    
    while (stmt.step()) {
      const row = stmt.getAsObject();
      const objectId = row.object_id as string;
      
      // Use pre-computed hashes from the objects table
      const contentHash = row.content_hash || '';
      const subtreeHash = row.subtree_hash || '0';
      const versionVector = row.version_vector ? JSON.parse(row.version_vector) : {};
      
      // Get properties for push operations (only when needed)
      const properties = await this.getObjectProperties(objectId);
      
      result[objectId] = {
        objectId,
        contentHash,
        subtreeHash,
        versionVector,
        properties // Include properties for push operations
      };
    }
    
    stmt.free();
    return result;
  }

  /**
   * Get entire subtree for an object
   */
  async getObjectSubtree(objectId: string): Promise<Record<string, any>> {
    const result: Record<string, any> = {};
    
    // Get all descendants using recursive CTE
    const sql = `
      WITH RECURSIVE subtree AS (
        SELECT object_id, parent_id, object_type, 0 as level
        FROM objects 
        WHERE object_id = ?
        
        UNION ALL
        
        SELECT o.object_id, o.parent_id, o.object_type, s.level + 1
        FROM objects o
        JOIN subtree s ON o.parent_id = s.object_id
      )
      SELECT object_id FROM subtree;
    `;
    
    const stmt = this.db.prepare(sql);
    stmt.bind([objectId]);
    
    while (stmt.step()) {
      const row = stmt.getAsObject();
      const objId = row.object_id as string;
      
      // Get properties for this object
      const properties = await this.getObjectProperties(objId);
      const contentHash = this.computeContentHash(properties);
      const versionVector = await this.getVersionVector(objId);
      const subtreeHash = await this.getSubtreeHash(objId);
      
      result[objId] = {
        objectId: objId,
        contentHash,
        subtreeHash,
        versionVector,
        properties
      };
    }
    
    stmt.free();
    return result;
  }

  /**
   * Get properties for specific objects
   */
  async getPropertiesForObjects(objectIds: string[]): Promise<ObjectProperty[]> {
    if (objectIds.length === 0) return [];
    
    const placeholders = objectIds.map(() => '?').join(',');
    const sql = `
      SELECT object_id, property, value, lww_counter, lww_random, 
             replica_id, logical_timestamp, updated_at, user_id
      FROM object_properties
      WHERE object_id IN (${placeholders})
      ORDER BY object_id, property;
    `;

    const stmt = this.db.prepare(sql);
    stmt.bind(objectIds);
    const result = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      result.push({
        object_id: row.object_id as string,
        property: row.property as string,
        value: row.value as string,
        lww_counter: row.lww_counter as number,
        lww_random: row.lww_random as number,
        replica_id: row.replica_id as number,
        logical_timestamp: row.logical_timestamp as number,
        updated_at: row.updated_at as number,
        user_id: row.user_id as string,
      });
    }
    stmt.free();
    return result;
  }

  /**
   * Compute content hash for properties (simplified)
   */
  private computeContentHash(properties: ObjectProperty[]): string {
    const content = properties
      .sort((a, b) => a.property.localeCompare(b.property))
      .map(p => `${p.property}:${p.value}:${p.lww_counter}:${p.lww_random}`)
      .join('|');
    
    // Simple hash function (in production, use crypto.subtle.digest)
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  /**
   * Get version vector for an object (latest timestamp per replica)
   */
  private async getVersionVector(objectId: string): Promise<Record<string, number>> {
    const sql = `
      SELECT replica_id, MAX(logical_timestamp) as max_timestamp
      FROM object_properties
      WHERE object_id = ?
      GROUP BY replica_id;
    `;
    
    const stmt = this.db.prepare(sql);
    stmt.bind([objectId]);
    const result: Record<string, number> = {};
    
    while (stmt.step()) {
      const row = stmt.getAsObject();
      result[row.replica_id as string] = row.max_timestamp as number;
    }
    
    stmt.free();
    return result;
  }

  /**
   * Get subtree hash (0 if no children, as per new design)
   */
  private async getSubtreeHash(objectId: string): Promise<string | null> {
    // Check if object has children
    const sql = `
      SELECT COUNT(*) as child_count
      FROM objects
      WHERE parent_id = ?;
    `;
    
    const stmt = this.db.prepare(sql);
    stmt.bind([objectId]);
    stmt.step();
    const row = stmt.getAsObject();
    const childCount = row.child_count as number;
    stmt.free();
    
    // Return 0 if no children (as per new design)
    if (childCount === 0) {
      return "0";
    }
    
    // Compute hash of children (simplified)
    const childSql = `
      SELECT object_id
      FROM objects
      WHERE parent_id = ?
      ORDER BY object_id;
    `;
    
    const childStmt = this.db.prepare(childSql);
    childStmt.bind([objectId]);
    const childIds: string[] = [];
    
    while (childStmt.step()) {
      const childRow = childStmt.getAsObject();
      childIds.push(childRow.object_id as string);
    }
    childStmt.free();
    
    // Simple hash of child IDs
    const content = childIds.join('|');
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(16);
  }

  async getObjectsSince(timestamp: number): Promise<ObjectEntity[]> {
    const sql = `
      SELECT object_id, parent_id, object_type, is_tombstoned, 
             created_at, created_by_replica, created_by_user
      FROM objects
      WHERE created_at > ?
      ORDER BY created_at;
    `;

    const stmt = this.db.prepare(sql);
    stmt.bind([timestamp]);
    const result = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      result.push({
        object_id: row.object_id as string,
        parent_id: row.parent_id as string || undefined,
        object_type: row.object_type as string,
        is_tombstoned: Boolean(row.is_tombstoned),
        created_at: row.created_at as number,
        created_by_replica: row.created_by_replica as number,
        created_by_user: row.created_by_user as string,
      });
    }
    stmt.free();
    return result;
  }

  private async loadDatabaseFromStorage(): Promise<Uint8Array | null> {
    try {
      // Check if OPFS is fully supported (including createWritable for Safari compatibility)
      if (await this.isOPFSFullySupported()) {
        const opfsRoot = await navigator.storage!.getDirectory();
        
        // Handle both old format (documents/file.db) and new format (file.sqlite)
        let fileHandle;
        if (this.databaseName && this.databaseName.startsWith('documents/')) {
          // New format: documents/file.db -> documents/file.sqlite
          const filename = this.databaseName.replace('documents/', '').replace('.db', '.sqlite');
          const documentsDir = await opfsRoot.getDirectoryHandle('documents', { create: false });
          fileHandle = await documentsDir.getFileHandle(filename, { create: false });
        } else {
          // Legacy format
          fileHandle = await opfsRoot.getFileHandle(`${this.databaseName}.sqlite`, { create: false });
        }
        
        const file = await fileHandle.getFile();
        const arrayBuffer = await file.arrayBuffer();
        return new Uint8Array(arrayBuffer);
      } else {
        return this.loadFromLocalStorage();
      }
    } catch (error: any) {
      if (error.name === 'NotFoundError') {
        return null;
      }
      console.error('Failed to load database from OPFS:', error);
      // Fallback to localStorage
      return this.loadFromLocalStorage();
    }
  }

  private loadFromLocalStorage(): Uint8Array | null {
    try {
      const stored = localStorage.getItem(`sqlite_db_${this.databaseName}`);
      if (stored) {
        const binaryString = atob(stored);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
    return null;
  }

  private async saveDatabaseToStorage(): Promise<void> {
    if (!this.db) return;
    
    try {
      const data = this.db.export();
      
      // Try OPFS first (with Safari compatibility check)
      if (await this.isOPFSFullySupported()) {
        await this.saveToOPFS(data);
      } else {
        // Fallback to localStorage
        console.warn('OPFS not fully supported (likely Safari), using localStorage');
        this.saveToLocalStorage(data);
      }
    } catch (error) {
      console.error('Failed to save database to OPFS, trying localStorage:', error);
      // Fallback to localStorage
      const data = this.db.export();
      this.saveToLocalStorage(data);
    }
  }

  private async saveToOPFS(data: Uint8Array): Promise<void> {
    const opfsRoot = await navigator.storage!.getDirectory();
    
    if (!this.databaseName) {
      throw new Error('Database name not initialized - call initialize() first');
    }
    
    if (this.databaseName.startsWith('documents/')) {
      // New format: save to documents/ subfolder
      const filename = this.databaseName.replace('documents/', '').replace('.db', '.sqlite');
      const documentsDir = await opfsRoot.getDirectoryHandle('documents', { create: true });
      const fileHandle = await documentsDir.getFileHandle(filename, { create: true });
      
      // Check if createWritable is supported (Safari compatibility)
      if (typeof fileHandle.createWritable !== 'function') {
        throw new Error('createWritable not supported - Safari compatibility issue');
      }
      
      const writable = await fileHandle.createWritable();
      await writable.write(data);
      await writable.close();
    } else {
      // Legacy format
      const fileHandle = await opfsRoot.getFileHandle(`${this.databaseName}.sqlite`, { create: true });
      
      // Check if createWritable is supported (Safari compatibility)
      if (typeof fileHandle.createWritable !== 'function') {
        throw new Error('createWritable not supported - Safari compatibility issue');
      }
      
      const writable = await fileHandle.createWritable();
      await writable.write(data);
      await writable.close();
    }
  }

  private async isOPFSFullySupported(): Promise<boolean> {
    try {
      // Check basic OPFS support
      if (typeof navigator === 'undefined' || !('storage' in navigator) || !navigator.storage || !('getDirectory' in navigator.storage)) {
        return false;
      }
      
      // Check if createWritable is supported (Safari often lacks this)
      const opfsRoot = await navigator.storage.getDirectory();
      const testHandle = await opfsRoot.getFileHandle('opfs-test', { create: true });
      
      // Safari might support getFileHandle but not createWritable
      if (typeof testHandle.createWritable !== 'function') {
        return false;
      }
      
      // Clean up test file
      await opfsRoot.removeEntry('opfs-test');
      return true;
    } catch (error) {
      console.debug('OPFS support check failed:', error);
      return false;
    }
  }

  private saveToLocalStorage(data: Uint8Array): void {
    try {
      const binaryString = String.fromCharCode.apply(null, Array.from(data));
      const base64 = btoa(binaryString);
      localStorage.setItem(`sqlite_db_${this.databaseName}`, base64);
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  async exportDatabase(): Promise<Uint8Array | null> {
    return this.db.export();
  }

  async saveDatabase(): Promise<void> {
    await this.saveDatabaseToStorage();
  }

  /**
   * Start batch mode - disables auto-save after each operation
   */
  startBatch(): void {
    this.batchMode = true;
  }

  /**
   * End batch mode and save database
   */
  async endBatch(): Promise<void> {
    this.batchMode = false;
    await this.autoComputeHashes();
    await this.saveDatabaseToStorage();
  }

  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  // Static method to list all document databases
  static async listDocumentDatabases(): Promise<Array<{
    documentId: string;
    filename: string;
    size: number;
    lastModified: Date;
  }>> {
    const databases: Array<{
      documentId: string;
      filename: string;
      size: number;
      lastModified: Date;
    }> = [];

    try {
      // Try OPFS first
      if (typeof navigator !== 'undefined' && 'storage' in navigator && navigator.storage && 'getDirectory' in navigator.storage) {
        const opfsRoot = await navigator.storage.getDirectory();
        
        try {
          const documentsDir = await opfsRoot.getDirectoryHandle('documents', { create: false });
          
          // @ts-ignore - TypeScript doesn't know about the async iterator
          for await (const [name, handle] of documentsDir.entries()) {
            if (handle.kind === 'file' && name.endsWith('.sqlite')) {
              const file = await handle.getFile();
              const documentId = name.replace('.sqlite', '');
              
              databases.push({
                documentId,
                filename: name,
                size: file.size,
                lastModified: new Date(file.lastModified)
              });
            }
          }
        } catch (error: any) {
          if (error.name !== 'NotFoundError') {
            console.warn('Error accessing documents directory:', error);
          }
        }
      }
      
      // Also check localStorage for legacy databases
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sqlite_db_documents/')) {
          const documentId = key.replace('sqlite_db_documents/', '').replace('.db', '');
          const data = localStorage.getItem(key);
          
          databases.push({
            documentId,
            filename: `${documentId}.db`,
            size: data ? data.length : 0,
            lastModified: new Date() // localStorage doesn't have lastModified
          });
        }
      }
      
    } catch (error) {
      console.error('Failed to list document databases:', error);
    }

    return databases.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());
  }

  // Static method to delete a document database locally (offload)
  static async deleteDocumentDatabase(documentId: string): Promise<boolean> {
    try {
      // Try OPFS first
      if (typeof navigator !== 'undefined' && 'storage' in navigator && navigator.storage && 'getDirectory' in navigator.storage) {
        const opfsRoot = await navigator.storage.getDirectory();
        
        try {
          const documentsDir = await opfsRoot.getDirectoryHandle('documents', { create: false });
          const filename = `${documentId}.sqlite`;
          await documentsDir.removeEntry(filename);
          return true;
        } catch (error: any) {
          if (error.name !== 'NotFoundError') {
            console.warn('Error deleting from OPFS:', error);
          }
        }
      }
      
      // Also try localStorage for legacy databases
      const legacyKey = `sqlite_db_documents/${documentId}.db`;
      if (localStorage.getItem(legacyKey)) {
        localStorage.removeItem(legacyKey);
        return true;
      }
      
      console.warn(`Database not found: ${documentId}`);
      return false;
      
    } catch (error) {
      console.error(`Failed to delete document database ${documentId}:`, error);
      return false;
    }
  }



  getClientState() {
    return clientStateManager.getClientState();
  }

  async getAllProperties(): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    try {
      const stmt = this.db.prepare(`
        SELECT 
          object_id,
          property,
          value,
          lww_counter,
          lww_random,
          logical_timestamp,
          replica_id,
          user_id,
          updated_at
        FROM object_properties 
        ORDER BY object_id, property
      `);
      
      const results = [];
      while (stmt.step()) {
        results.push(stmt.getAsObject());
      }
      stmt.free();
      
      return results;
    } catch (error) {
      console.error('Failed to get all properties:', error);
      throw error;
    }
  }

  async getStorageInfo(): Promise<string> {
    try {
      if (typeof navigator !== 'undefined' && 'storage' in navigator && navigator.storage && 'getDirectory' in navigator.storage) {
        if ('estimate' in navigator.storage) {
          const estimate = await navigator.storage.estimate();
          const usedMB = ((estimate.usage || 0) / 1024 / 1024).toFixed(2);
          const quotaMB = ((estimate.quota || 0) / 1024 / 1024).toFixed(2);
          return `OPFS: ${usedMB}MB used / ${quotaMB}MB available`;
        } else {
          return 'OPFS: Available (quota unknown)';
        }
      } else {
        return 'Web: Using localStorage fallback';
      }
    } catch (error) {
      console.error('Failed to check storage info:', error);
      return 'Storage info unavailable';
    }
  }
}

// Singleton instance
export const sqliteService = new SQLiteService();
